import { Component, EventEmitter, Output, ChangeDetectionStrategy, ViewChild, ElementRef } from '@angular/core';
import { LegalDocumentsService, LegalTemplate } from 'src/app/services/legal-documents.service';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Safe } from '../../../shared/pipes/safeHTML';


type DocKey = 'privacy' | 'terms';

interface LegalDoc {
  key: DocKey;
  label: string;
  template: LegalTemplate;
  html: string;
  loading: boolean;
  failed: boolean;
}

@Component({
  selector: 'app-legal-documents',
  templateUrl: './legal-documents.component.html',
  styleUrl: './legal-documents.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [Safe]
})
export class LegalDocumentsComponent {
  /**
   * Emits once the user has ticked the box and confirmed both documents.
   * Carries a callback the listener must invoke to dismiss the dialog, so the
   * modal stays visible until any capture of it has finished.
   */
  @Output() onAccept = new EventEmitter<() => void>();
  /** Emits when the user backs out without accepting. */
  @Output() onDecline = new EventEmitter<void>();

  /** The scrollable pane — the consent capture screenshots this. */
  @ViewChild('docBody') docBody?: ElementRef<HTMLElement>;

  /** Business name shown in the acceptance line; set by the caller. */
  businessName = '';

  accepted = false;
  /**
   * Set once the user has scrolled a document through to the end. Reading one
   * is enough to unlock acceptance.
   */
  readTerms = false;
  /** Documents the user has actually read to the end, by key. */
  private readDocs = new Set<DocKey>();
  /** Whether the pane is at its end right now — drives the confirmation note. */
  atEnd = false;
  private closedByAction = false;
  /** Guards against a second Accept while the capture is still running. */
  accepting = false;

  // Terms first: it is the document that carries the acceptance, and the flow ends on it.
  docs: LegalDoc[] = [
    { key: 'terms', label: 'Terms & Conditions', template: 'TERMS_AND_CONDITION', html: '', loading: true, failed: false },
    { key: 'privacy', label: 'Privacy Policy', template: 'PRIVACY_POLICY', html: '', loading: true, failed: false }
  ];

  active: DocKey = 'terms';

  constructor(private bsModalRef: BsModalRef, private _legal: LegalDocumentsService) { }

  ngOnInit(): void {
    this.docs.forEach(doc => this.load(doc));
  }

  get current(): LegalDoc {
    return this.docs.find(d => d.key === this.active) || this.docs[0];
  }

  /**
   * The box only unlocks once the Terms have been read to the end — reaching
   * the tab is not enough.
   */
  get canAccept(): boolean {
    return this.hasName && this.readTerms && this.docs.every(d => !!d.html);
  }

  /**
   * Consent is given by a named person, so without a name there is nobody to
   * record it against. The documents stay readable, but acceptance is hidden
   * until the caller supplies one.
   */
  get hasName(): boolean {
    return !!(this.businessName || '').trim();
  }

  private load(doc: LegalDoc): void {
    doc.loading = true;
    doc.failed = false;

    // Usually already resolved: the register page warms both documents on
    // load, so this replays the cached value and the modal opens with content.
    this._legal.get(doc.template).subscribe({
      next: html => {
        doc.html = this.prepareContent(html);
        doc.loading = false;
        // Terms is the tab the modal opens on, so nothing has called select()
        // to evaluate the scroll position. Without this a short document — one
        // that fits the pane and never scrolls — would leave the checkbox locked.
        if (doc.key === this.active) {
          setTimeout(() => this.checkScrolledToEnd());
        }
      },
      error: () => {
        doc.failed = true;
        doc.loading = false;
      }
    });
  }

  /**
   * Prepares the stored template for display: links open in a new tab, and
   * blank spacer paragraphs are dropped.
   *
   * The modal sits in the middle of registration, so following a link in place
   * would abandon the form. `rel` is set alongside `target` because a plain
   * `_blank` hands the opened page a reference back to this window.
   *
   * Parsed rather than regex-replaced so unusual markup in the stored template
   * cannot produce a broken tag.
   */
  private prepareContent(html: string): string {
    if (!html) {
      return html;
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('a[href]').forEach(anchor => {
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener noreferrer');
    });
    // The stored templates use runs of blank paragraphs as spacing, which open
    // a tall empty band mid-document. Dropped here rather than in CSS so it
    // does not depend on `:has()` support.
    doc.querySelectorAll('p').forEach(p => {
      const blank = !p.textContent?.trim() && !p.querySelector('img, table');
      if (blank) {
        p.remove();
      }
    });
    return doc.body.innerHTML;
  }

  retry(doc: LegalDoc): void {
    this._legal.invalidate(doc.template);
    this.load(doc);
  }

  /**
   * Jumps the reader to the end of the current document. Smooth so it is clear
   * the page moved rather than swapped, and it still passes through the scroll
   * handler, so reaching the bottom counts as read exactly as if the user had
   * scrolled there.
   */
  scrollToBottom(): void {
    const pane = this.docBody?.nativeElement;
    if (!pane) {
      return;
    }
    pane.scrollTo({ top: pane.scrollHeight, behavior: 'smooth' });
  }

  /**
   * A document is reachable only once every document before it has been read
   * through. Keeps the order: Terms first, then the Privacy Policy.
   */
  isUnlocked(key: DocKey): boolean {
    const i = this.docs.findIndex(d => d.key === key);
    return this.docs.slice(0, i).every(d => this.readDocs.has(d.key));
  }

  select(key: DocKey): void {
    if (!this.isUnlocked(key)) {
      return;
    }
    this.active = key;
    // A document that failed during preload gets another chance the moment
    // its tab is actually opened.
    const doc = this.current;
    if (doc.failed && !doc.loading) {
      this.retry(doc);
    }
    // Each document starts from its own top rather than inheriting the
    // previous tab's scroll position.
    if (this.docBody) {
      this.docBody.nativeElement.scrollTop = 0;
    }
    // A short document, or a large window, may leave nothing to scroll.
    setTimeout(() => this.checkScrolledToEnd());
  }

  /**
   * Hands control to the listener while the dialog is still on screen — the
   * consent capture has to photograph this view, ticked checkbox included —
   * and only closes once it reports back.
   */
  /** Bound to the document pane's scroll event. */
  onBodyScroll(): void {
    this.checkScrolledToEnd();
  }

  /**
   * Tracks whether the pane is scrolled to its end. `atEnd` follows the
   * scroll position live so the confirmation note comes and goes, while
   * `readTerms` latches — once a document has been read the checkbox stays
   * usable rather than re-locking when the user scrolls back up.
   *
   * Either document counts: both are presented together and accepted by the
   * same tick, so reaching the end of either one is enough to unlock it.
   * Reaching the end also unlocks the next document's tab.
   *
   * The tolerance absorbs fractional scroll heights from browser zoom and
   * hi-dpi displays, where scrollTop never quite reaches the exact maximum.
   */
  private checkScrolledToEnd(): void {
    const pane = this.docBody?.nativeElement;
    if (!pane || !this.current.html) {
      this.atEnd = false;
      return;
    }
    this.atEnd = pane.scrollHeight - pane.scrollTop - pane.clientHeight <= 24;
    if (!this.atEnd) {
      return;
    }
    this.readTerms = true;

    // Reaching the end unlocks the next document but does not jump to it: the
    // reader stays where they are and switches tabs when they choose to.
    this.readDocs.add(this.active);
  }

  /**
   * The row, not the input, carries the click: the checkbox is display:none and
   * its box is drawn by the adjacent label, so label-for cannot be relied on to
   * reach it. Ignored while a capture is running, since the recorded image has
   * to match the state that was accepted.
   */
  toggleAccepted(): void {
    if (this.accepting) {
      return;
    }
    this.accepted = !this.accepted;
  }

  accept(): void {
    if (!this.accepted || !this.canAccept || this.accepting) {
      return;
    }
    this.accepting = true;
    this.closedByAction = true;
    this.onAccept.emit(() => this.bsModalRef.hide());
  }

  decline(): void {
    this.closedByAction = true;
    this.onDecline.emit();
    this.bsModalRef.hide();
  }

  /** Dismissing with the X or the backdrop is not acceptance. */
  close(): void {
    if (!this.closedByAction) {
      this.closedByAction = true;
      this.onDecline.emit();
    }
    this.bsModalRef.hide();
  }
}
